// Director State Machine
// Spec-driven: combines admin-controlled phases with time-based defaults.
//
// Phases (from backend `event_state.phase`):
//   "auto"            → time-based default flow (pre-event → sky/memories loop)
//   "cake_prep"       → admin pressed Start Cake Cutting (buildup messages)
//   "cake_celebrate"  → fireworks celebration (auto-advanced)
//   "dancing"         → dance floor (auto-advanced)
//   "dinner"          → admin pressed Start Dinner (Anagha's messages loop)
//   "farewell"        → forced final cinematic

import { formatEventDate, formatEventTime } from "./director";
export { formatEventDate, formatEventTime };

export const PRE_MESSAGES = [
  { te: "రాజ్య ద్వారాలు త్వరలోనే తెరుచుకుంటాయి...", en: "The Kingdom Gates Will Open Soon..." },
  { te: "యువరాణి అనఘ ఒక మాయా సాయంత్రాన్ని సిద్ధం చేస్తోంది...", en: "Princess Anagha Is Preparing A Magical Evening..." },
  { te: "ఒక రాజవేడుక మీ కోసం ఎదురుచూస్తోంది...", en: "A Royal Celebration Awaits..." },
  { te: "రాజ్యం మిమ్మల్ని స్వాగతించడానికి సిద్ధమవుతోంది...", en: "The Kingdom Is Getting Ready To Welcome You..." },
  { te: "ఏదో అద్భుతం మొదలవ్వబోతోంది...", en: "Something Wonderful Is About To Begin..." },
];

// Anticipation messages played right after the Doors open (show start).
// The princess is asking "who is coming to my party?" — 20 sec of build-up
// before we transition into the Sky of Stars view.
export const ANTICIPATION_MESSAGES = [
  { te: "రండి రండి... నా పార్టీకి ఎవరెవరు వస్తున్నారో చూద్దాం!",
    en: "Come, come… let's see who is visiting my party!" },
  { te: "అనఘ రాజ్యంలోకి కొత్త అతిథులు అడుగుపెడుతున్నారు...",
    en: "New guests are stepping into Anagha's Kingdom..." },
  { te: "ఎంతమంది ప్రియమైన స్నేహితులు వచ్చారో లెక్కపెడదాం!",
    en: "Let's count how many dear friends have arrived!" },
  { te: "ఆకాశంలో ఒక్కో నక్షత్రం మెరుస్తోంది... మీది ఏది?",
    en: "One by one, stars are twinkling in the sky… which one is yours?" },
  { te: "మీ ముఖం ఇప్పుడే ఆకాశంలోకి చేరబోతోంది!",
    en: "Your face is about to join the royal sky!" },
];
export const ANTICIPATION_DURATION = 22; // seconds — first block after doors

export const CAKE_PREP_MESSAGES = [
  { te: "ఏదో అద్భుతం జరగబోతోంది...",                 en: "Something Magical Is About To Happen..." },
  { te: "యువరాణి ప్రత్యేక ధన్యవాదాలు సిద్ధం చేస్తోంది...", en: "The Princess Is Preparing A Special Thank You..." },
  { te: "రాజవేడుక త్వరలోనే మొదలవుతుంది...",            en: "A Royal Celebration Begins Soon..." },
  { te: "రాజ్యం తన అతిపెద్ద క్షణం కోసం ఎదురుచూస్తోంది...", en: "The Kingdom Awaits Its Biggest Moment..." },
];

export const ARRIVAL_HEADERS = [
  { te: "✨ అనఘ రాజ్యంలోకి కొత్త నక్షత్రం చేరింది ✨", en: "✨ A New Star Has Entered Anagha's Kingdom ✨" },
  { te: "🏰 రాజ్యానికి స్వాగతం",                       en: "🏰 Welcome To The Kingdom" },
  { te: "⭐ యువరాణి అనఘ ఆకాశంలో మరో వెలుగు ⭐",      en: "⭐ A New Light Joins Princess Anagha's Sky" },
  { te: "💫 మరో ప్రత్యేక అతిథి విచ్చేశారు",            en: "💫 Another Special Guest Has Arrived" },
];

// Auto-advance timings (seconds) for cake celebration → dancing transition.
export const CAKE_PREP_DURATION = 90;   // buildup before celebration
export const CAKE_CELEBRATE_DURATION = 60; // fireworks then advance to dancing
export const SKY_DURATION = 60;          // Sky of Stars cycle
export const MEMORIES_DURATION = 60;     // Memories pair cycle
export const ARRIVAL_DURATION = 45;      // arrival overlay seconds

/**
 * Given current time + config + state, return a "directive" the Director should render.
 * Returns: { mode, payload? }
 *  mode ∈ "pre" | "sky" | "memories" | "cake_prep" | "cake_celebrate" |
 *         "dancing" | "dinner" | "farewell" | "post"
 */
export function computeDirective({ now, config, state }) {
  if (!config) return { mode: "pre" };
  const startMs = new Date(config.start).getTime();
  const endMs   = new Date(config.end).getTime();
  const phase = state?.phase || "auto";
  const phaseStarted = state?.phase_started_at ? new Date(state.phase_started_at).getTime() : null;
  const phaseAge = phaseStarted ? (now - phaseStarted) / 1000 : null; // seconds in current phase

  // Force-overrides from admin
  if (phase === "show_running") {
    // First ~3 seconds → animated doors opening transition
    if (phaseAge !== null && phaseAge < 3) {
      return { mode: "doors_open" };
    }
    // Next 22 seconds → anticipation ("who is visiting my party?")
    if (phaseAge !== null && phaseAge < 3 + ANTICIPATION_DURATION) {
      return { mode: "anticipation" };
    }
    // After that, run the sky/memories rotation off phase_started_at
    const elapsedSec = (phaseAge ?? 0) - 3 - ANTICIPATION_DURATION;
    const cycleLen = SKY_DURATION + MEMORIES_DURATION;
    const inCycle = elapsedSec % cycleLen;
    if (inCycle < SKY_DURATION) return { mode: "sky" };
    return { mode: "memories", payload: { pageIdx: Math.floor(elapsedSec / cycleLen) } };
  }
  if (phase === "memories_show") {
    // Continuous memory slideshow triggered by "Play Memories" button.
    // pageIdx advances every MEMORIES_DURATION so photos rotate automatically.
    const elapsedSec = phaseAge ?? 0;
    return { mode: "memories", payload: { pageIdx: Math.floor(elapsedSec / MEMORIES_DURATION) } };
  }
  if (phase === "cake_prep") {
    if (phaseAge !== null && phaseAge >= CAKE_PREP_DURATION) {
      // auto-advance to celebration (frontend will POST to flip the state)
      return { mode: "cake_celebrate", autoAdvanceFrom: "cake_prep" };
    }
    return { mode: "cake_prep" };
  }
  if (phase === "cake_celebrate") {
    if (phaseAge !== null && phaseAge >= CAKE_CELEBRATE_DURATION) {
      return { mode: "dancing", autoAdvanceFrom: "cake_celebrate" };
    }
    return { mode: "cake_celebrate" };
  }
  if (phase === "dancing") return { mode: "dancing" };
  if (phase === "dinner")  return { mode: "dinner" };
  if (phase === "farewell") return { mode: "farewell" };

  // Auto flow
  if (now < startMs) return { mode: "pre" };
  if (now >= endMs) return { mode: "farewell" };

  // Sky/Memories alternating rotation
  const elapsed = (now - startMs) / 1000;
  const cycleLen = SKY_DURATION + MEMORIES_DURATION;
  const inCycle = elapsed % cycleLen;
  if (inCycle < SKY_DURATION) {
    return { mode: "sky" };
  }
  // Which pair of memory photos to show — pages of 2
  const cycleIdx = Math.floor(elapsed / cycleLen);
  return { mode: "memories", payload: { pageIdx: cycleIdx } };
}
